import { useState, useEffect } from 'react';
import { useAppDispatch, useAppSelector } from '@/store/hooks';
import { setHasApiKey } from '@/store/slices/authSlice';
import { settingsApi } from './api';
import { ApiKeyCard, MetafieldConfigCard, type ShopSettings } from './components';

export function SettingsPage() {
  const dispatch = useAppDispatch();
  const shop = useAppSelector((state) => state.auth.shop);
  const [isLoading, setIsLoading] = useState(false);
  const [isLoadingSettings, setIsLoadingSettings] = useState(true);
  const [isSavingSettings, setIsSavingSettings] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [newlyCreatedKey, setNewlyCreatedKey] = useState<string | null>(null);
  const [settings, setSettings] = useState<ShopSettings | null>(null);

  useEffect(() => {
    const loadSettings = async () => {
      try {
        const data = await settingsApi.getSettings();
        setSettings(data);
      } catch (err) {
        setError(err instanceof Error ? err.message : 'Failed to load settings');
      } finally {
        setIsLoadingSettings(false);
      }
    };
    loadSettings();
  }, []);

  const handleCreateOrRotate = async () => {
    setIsLoading(true);
    setError(null);
    try {
      const response = await settingsApi.rotateAPIKey();
      setNewlyCreatedKey(response.apiKey);
      dispatch(setHasApiKey(true));
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to generate API key');
    } finally {
      setIsLoading(false);
    }
  };

  const handleDismissNewKey = () => {
    setNewlyCreatedKey(null);
  };

  const handleSaveSettings = async (newSettings: ShopSettings) => {
    setIsSavingSettings(true);
    setError(null);
    try {
      const updated = await settingsApi.updateSettings(newSettings);
      setSettings(updated);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to save settings');
    } finally {
      setIsSavingSettings(false);
    }
  };

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-2xl font-semibold">Settings</h1>
        <p className="text-muted-foreground">
          Manage your API credentials and integration settings
        </p>
      </div>

      {error && (
        <div className="rounded-md bg-destructive/10 p-3 text-sm text-destructive">
          {error}
        </div>
      )}

      {!isLoadingSettings && (
        <MetafieldConfigCard
          settings={settings}
          onSave={handleSaveSettings}
          isLoading={isSavingSettings}
        />
      )}

      <ApiKeyCard
        hasApiKey={shop?.hasApiKey || false}
        newlyCreatedKey={newlyCreatedKey}
        onCreateOrRotate={handleCreateOrRotate}
        onDismissNewKey={handleDismissNewKey}
        isLoading={isLoading}
      />
    </div>
  );
}
